import { DynamicForm } from "@shared/components/DynamicForm";
import { PageHeader } from "@shared/components/PageHeader";
import { FormField, FormService } from "@shared/services/FormServices";
import { ArrowLeft } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CustomersmemberList from "./customerMemberList";
import BusinessUserMemberList from "./businessUserMemberList";
import { useGetChoices } from "../../shared/hooks";
import {
  useCreateRecipientList,
  useGetAllSegments,
  useGetRecipientDetails,
  useUpdateRecipientList,
} from "../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

const CreateRecipientList = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const { id, action } = useParams();
  const dataLoadedRef = useRef(false);

  const [formData, setFormData] = useState({
    listName: "",
    tags: [],
    audience: "",
    segment: "",
    ListType: "",
  });

  const [preSelectedIds, setPreSelectedIds] = useState<string[]>([]);
  const [audienceType, setAudienceType] = useState<string>("");

  // Filter states for Customers - now supporting arrays
  const [customerFilters, setCustomerFilters] = useState({
    area: [] as string[],
    subArea: [] as string[],
    category: [] as string[],
    subCategory: [] as string[],
    status: "",
  });

  // Filter states for Business Users - now supporting arrays
  const [businessUserFilters, setBusinessUserFilters] = useState({
    department: [] as string[],
    role: [] as string[],
    status: "",
  });

  const { data: getAudienceType } = useGetChoices(
    "audience_type",
    "quick_messaging"
  );
  const { data: getListType } = useGetChoices("list_type");
  const { data: segment } = useGetAllSegments(audienceType);
  const { data: listData } = useGetRecipientDetails(Number(id));

  const createRecipientList = useCreateRecipientList();
  const updateRecipientList = useUpdateRecipientList(Number(id));

  const mapChoices = (data: any) =>
    data?.result?.map((item: any) => ({
      value: item.key,
      label: item.value,
    })) || [];

  const audienceTypeOptions = useMemo(
    () => mapChoices(getAudienceType),
    [getAudienceType]
  );
  const listTypeOptions = useMemo(() => mapChoices(getListType), [getListType]);

  const segmentOption = useMemo(
    () =>
      segment?.result?.map((choice) => ({
        value: choice.id,
        label: choice.name,
      })) || [],
    [segment]
  );

  const formFields: FormField[] = [
    {
      name: "listName",
      type: "text",
      label: "List Name",
      placeholder: "e.g., Q1 2024 Business Users",
      groupColorNeeded: false,
      group: "List Details",
      required: true,
      helperText:
        "Choose a descriptive name to easily identify this recipient list",
      className: "col-span-2 mt-2",
    },
    {
      name: "tags",
      type: "tags",
      label: "Tags",
      placeholder: "e.g., billing, urgent, monthly",
      required: false,
      groupColorNeeded: false,
      group: "List Details",
      helperText:
        "Add tags to organize and filter lists (e.g., 'urgent', 'Q1-2024')",
      className: "col-span-2",
    },
    {
      name: "audience",
      type: "select",
      label: "Audience",
      options: audienceTypeOptions,
      required: true,
      groupColorNeeded: false,
      group: "List Details",
      helperText:
        "Select the type of recipients you want to include in this list",
      className: "col-span-1",
    },
    {
      name: "segment",
      type: "select",
      label: "Segment",
      options: segmentOption,
      required: false,
      groupColorNeeded: false,
      group: "List Details",
      helperText:
        "Optionally select a segment to pre-filter recipients based on saved criteria",
      className: "col-span-1",
    },
    {
      name: "ListType",
      type: "select",
      label: "List Type",
      options: listTypeOptions,
      required: true,
      groupColorNeeded: false,
      group: "List Details",
      helperText:
        "Static lists have fixed members selected at creation. Dynamic lists automatically update based on criteria.",
      className: "col-span-2",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  // Data initialization effect - including filter auto-population
  useEffect(() => {
    if (dataLoadedRef.current) return;

    switch (action) {
      case "edit":
        if (listData) {
          const editData = {
            listName: listData.name || "",
            tags: listData.tags || [],
            audience: listData.audienceType?.toString() || "",
            segment: listData.segment?.toString() || "",
            ListType: listData.filterType?.toString() || "",
          };

          // Set pre-selected recipient IDs for static lists
          const recipientIds =
            listData.recipientData?.recipientIds?.map((id) => String(id)) || [];
          setPreSelectedIds(recipientIds);

          // Auto-populate filters for dynamic lists
          if (listData.filters) {
            // For Customer filters
            if (listData.audienceType === "1") {
              setCustomerFilters({
                area: listData.filters.area || [],
                subArea: listData.filters.subarea || [],
                category: listData.filters.category || [],
                subCategory: listData.filters.sub_category || [],
                status: listData.filters.status || "",
              });
            }
            // For Business User filters
            else if (listData.audienceType === "2") {
              setBusinessUserFilters({
                department: listData.filters.department || [],
                role: listData.filters.role || [],
                status: listData.filters.status || "",
              });
            }
          }

          setFormData(editData);
          setAudienceType(editData.audience);
          FormService.populateForm(form, editData, formFields);
          dataLoadedRef.current = true;
        }
        break;

      default:
        dataLoadedRef.current = true;
        break;
    }
  }, [action, listData, form, formFields]);

  const handleFieldChange = (field: string, value: any) => {
    switch (field) {
      case "audience":
        setAudienceType(value);
        // Reset filters when audience changes
        setCustomerFilters({
          area: [],
          subArea: [],
          category: [],
          subCategory: [],
          status: "",
        });
        setBusinessUserFilters({
          department: [],
          role: [],
          status: "",
        });
        break;
      case "ListType":
        // Reset filters when list type changes
        setCustomerFilters({
          area: [],
          subArea: [],
          category: [],
          subCategory: [],
          status: "",
        });
        setBusinessUserFilters({
          department: [],
          role: [],
          status: "",
        });
        setPreSelectedIds([]);
        break;
      default:
        break;
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle filter changes from child components
  const handleCustomerFilterChange = (filters: typeof customerFilters) => {
    setCustomerFilters(filters);
  };

  const handleBusinessUserFilterChange = (
    filters: typeof businessUserFilters
  ) => {
    setBusinessUserFilters(filters);
  };

  // Simplified submit handler - receives complete payload from child
  const handleSubmit = async (payload: any) => {
    try {
      if (action === "edit") {
        await updateRecipientList.mutateAsync(payload);
      } else {
        await createRecipientList.mutateAsync(payload);
      }
      navigate("/communication-hub/recipient-list");
    } catch (error) {}
  };

  const handleCancel = () => {
    form.reset();
    navigate("/communication-hub/recipient-list");
  };

  // Render member list based on audience selection
  const renderMemberList = () => {
    const audienceValue = form.watch("audience");

    // Pass form values to child components
    const commonProps = {
      form,
      onSubmit: handleSubmit,
      onCancel: handleCancel,
      formValues: {
        listName: form.watch("listName"),
        tags: form.watch("tags"),
        audience: form.watch("audience"),
        segment: form.watch("segment"),
        listType: form.watch("ListType"),
      },
      remoteUtilityId: Number(remoteUtilityId),
      preSelectedIds: preSelectedIds,
      action: action,
    };

    switch (audienceValue) {
      case "1":
        return (
          <CustomersmemberList
            {...commonProps}
            filters={customerFilters}
            onFilterChange={handleCustomerFilterChange}
          />
        );
      case "2":
        return (
          <BusinessUserMemberList
            {...commonProps}
            filters={businessUserFilters}
            onFilterChange={handleBusinessUserFilterChange}
          />
        );
      default:
        return (
          <div className="border border-gray-200 rounded-lg p-6 bg-white mt-2">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                Select Members
              </h2>
              <p className="text-sm text-gray-500">
                Use segmentation to filter and select specific recipients
              </p>
            </div>
            <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
              <h3 className="text-base font-semibold text-gray-700 mb-2">
                Please select an audience type to view available members
              </h3>
              <p className="text-sm text-gray-500">
                Choose an audience type from the left panel to start selecting
                recipients
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div>
      <div className="flex items-center gap-4">
        <ArrowLeft
          className="w-5 h-5 cursor-pointer text-gray-700 hover:text-gray-900"
          onClick={() => navigate("/communication-hub/recipient-list")}
        />
        <div className="flex flex-col">
          <PageHeader
            title={
              action === "edit"
                ? "Edit Recipient List"
                : "Create Recipient List"
            }
            subtitle={
              action === "edit"
                ? "Update recipient list details and members"
                : "Build a new recipient list by selecting members and adding details"
            }
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4 mt-2">
          <DynamicForm
            fields={formFields}
            form={form}
            gridColumns={2}
            responsiveColumns={{ sm: 1, md: 2 }}
            onSubmit={handleSubmit}
            onReset={handleCancel}
            onFieldChange={handleFieldChange}
            config={{
              resetButtonText: "Cancel",
              submitButtonText: "Save Segment",
              layout: "grid",
              columns: 2,
              showSubmitButton: false,
              showResetButton: false,
              spacing: "medium",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700",
                buttonContainer: "flex justify-end gap-2",
                input:
                  "w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                select:
                  "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
              },
            }}
          />
        </div>
        <div className="col-span-8">{renderMemberList()}</div>
      </div>
    </div>
  );
};

export default CreateRecipientList;
